<template>
  <div class="loading-container" :class="containerClasses">
    <div class="loading-content">
      <!-- Spinner Moderno com Múltiplos Anéis -->
      <div class="spinner-wrapper" :class="spinnerWrapperClasses">
        <div class="spinner-ring" :class="`ring-1 ring-${props.size} ring-${props.color}`"></div>
        <div class="spinner-ring" :class="`ring-2 ring-${props.size} ring-${props.color}`"></div>
        <div class="spinner-ring" :class="`ring-3 ring-${props.size} ring-${props.color}`"></div>
      </div>
      
      <!-- Text -->
      <p v-if="text" class="loading-text">
        {{ text }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps({
  text: {
    type: String,
    default: 'Carregando...'
  },
  fullScreen: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'md',
    validator: (value: string) => ['sm', 'md', 'lg', 'xl'].includes(value)
  },
  color: {
    type: String,
    default: 'primary',
    validator: (value: string) => ['primary', 'secondary', 'success', 'warning', 'error'].includes(value)
  }
})

// Classes do container
const containerClasses = computed(() => {
  const base = 'loading-container'
  const fullScreen = props.fullScreen ? 'loading-fullscreen' : ''
  return [base, fullScreen].filter(Boolean).join(' ')
})

// Classes do spinner wrapper
const spinnerWrapperClasses = computed(() => {
  return `spinner-wrapper spinner-wrapper-${props.size}`
})
</script>

<style scoped>
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xl, 32px);
  animation: fadeIn 0.3s ease-in;
}

.loading-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(13, 17, 23, 0.85);
  z-index: 1050;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md, 16px);
}

/* Spinner Wrapper */
.spinner-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.spinner-wrapper-sm {
  width: 20px;
  height: 20px;
}

.spinner-wrapper-md {
  width: 40px;
  height: 40px;
}

.spinner-wrapper-lg {
  width: 56px;
  height: 56px;
}

.spinner-wrapper-xl {
  width: 72px;
  height: 72px;
}

/* Spinner Rings */
.spinner-ring {
  position: absolute;
  border-radius: 50%;
  border-style: solid;
  border-color: transparent;
  animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

/* Tamanhos dos anéis */
.ring-sm {
  width: 20px;
  height: 20px;
  border-width: 2px;
}

.ring-md {
  width: 40px;
  height: 40px;
  border-width: 3px;
}

.ring-lg {
  width: 56px;
  height: 56px;
  border-width: 4px;
}

.ring-xl {
  width: 72px;
  height: 72px;
  border-width: 5px;
}

/* Cores dos anéis */
.ring-primary {
  border-top-color: var(--bloop-green, #7ED321);
  border-right-color: rgba(126, 211, 33, 0.3);
}

.ring-secondary {
  border-top-color: var(--bloop-text-secondary, #8E8E93);
  border-right-color: rgba(142, 142, 147, 0.3);
}

.ring-success {
  border-top-color: var(--bloop-success, #34C759);
  border-right-color: rgba(52, 199, 89, 0.3);
}

.ring-warning {
  border-top-color: var(--bloop-warning, #FF9500);
  border-right-color: rgba(255, 149, 0, 0.3);
}

.ring-error {
  border-top-color: var(--bloop-error, #FF3B30);
  border-right-color: rgba(255, 59, 48, 0.3);
}

/* Delays para efeito cascata */
.ring-1 {
  animation-delay: 0s;
}

.ring-2 {
  animation-delay: 0.15s;
  opacity: 0.7;
}

.ring-3 {
  animation-delay: 0.3s;
  opacity: 0.5;
}

/* Efeito glow para primary */
.ring-primary.ring-1 {
  filter: drop-shadow(0 0 8px rgba(126, 211, 33, 0.4));
}

.ring-primary.ring-2 {
  filter: drop-shadow(0 0 6px rgba(126, 211, 33, 0.3));
}

.ring-primary.ring-3 {
  filter: drop-shadow(0 0 4px rgba(126, 211, 33, 0.2));
}

/* Texto */
.loading-text {
  color: var(--bloop-text-secondary, #8E8E93);
  font-size: var(--font-size-sm, 14px);
  font-weight: 500;
  margin: 0;
  text-align: center;
  font-family: var(--font-family, 'Inter', sans-serif);
  letter-spacing: 0.01em;
  animation: fadeIn 0.4s ease-in 0.1s both;
}

/* Animações */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Efeito de pulso para fullscreen */
.loading-fullscreen .spinner-wrapper {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .loading-container {
    padding: var(--spacing-lg, 24px);
  }
  
  .loading-text {
    font-size: var(--font-size-xs, 12px);
  }
  
  .spinner-wrapper-xl {
    width: 56px;
    height: 56px;
  }
  
  .ring-xl {
    width: 56px;
    height: 56px;
    border-width: 4px;
  }
}

/* Melhorias de acessibilidade */
.loading-container {
  will-change: opacity;
}

.spinner-ring {
  will-change: transform;
}
</style>
